import React, { FC, useState, useEffect } from 'react';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { updateInternalDeliveryDateTemplate } from 'apollo/templates/delivery-schedule';
import IntExtDeliveryDatePicker from 'components/orders/order-details/int-ext-delivery-date-picker.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Order } from 'models/order';
import { LabClient } from 'models/client-directory';
import { getDeliveryAdvanceWorkingDate, WorkingDayBeforeCounter } from 'utils/internal-delivery-advance-working-date';
import { ApolloCache, Reference } from '@apollo/client';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import Loader from 'components/shared/loader/loader.component';
import { Box, Button } from '@mui/material';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type ChangeInternalDeliveryDateDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
    isUpdateCache?: boolean;
};

const ChangeInternalDeliveryDateDialog: FC<ChangeInternalDeliveryDateDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
    isUpdateCache,
}: ChangeInternalDeliveryDateDialogProps) => {
    const { t: translate } = useTranslation(['common', 'delivery-schedule', 'orders']);
    const [internalDeliveryDate, setInternalDeliveryDate] = useState<Date | null>(null);
    const [internalDeliveryDaysAdvance, setInternalDeliveryDaysAdvance] = useState<number>();
    const { showBoundary } = useDenthubErrorBoundary();

    const [childClientSettings, setChildClientSettings] = useState<LabClient>({} as LabClient);
    const [getLabClientById, { loading: labClientByIdLoading }] = useLazyQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ labClientById: childClientSettings }: { labClientById: LabClient }) => {
            setChildClientSettings(childClientSettings);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const updateCache = (cache: ApolloCache<Order>, order: Order) => {
        cache.modify({
            fields: {
                deliverySchedule(orderRefs: Reference | (Order | Reference | undefined)[], { toReference, readField }) {
                    if (internalDeliveryDate && internalDeliveryDate < new Date(new Date().setHours(12, 0, 0, 0))) {
                        const lateOrdersCount = readField('lateOrdersCount', orderRefs as Reference) as number;
                        if (lateOrdersCount) {
                            const count = lateOrdersCount + 1;
                            return { ...orderRefs, lateOrdersCount: count };
                        }
                    }
                    const plannedOrders = readField('plannedOrders', orderRefs as Reference) as Order[];
                    if (plannedOrders instanceof Array) {
                        return [...plannedOrders, toReference(order)];
                    }
                    return [plannedOrders, toReference(order)];
                },
                deliveryScheduleNoDeadlineOrders(
                    existingNoDeadlineOrderRefs: Reference | (Reference | undefined)[],
                    { readField },
                ) {
                    if (existingNoDeadlineOrderRefs instanceof Array) {
                        return [...existingNoDeadlineOrderRefs].filter((noDeadlineOrderRef: Reference | undefined) => {
                            return order._id !== readField('_id', noDeadlineOrderRef);
                        });
                    }
                    return [];
                },
            },
        });
    };

    useSeedOnOpen(open, () => {
        if (order) {
            if (order.deliveryDetails?.internalDeliveryDate) {
                setInternalDeliveryDate(order.deliveryDetails.internalDeliveryDate);
                const deliveryDate = new Date(order.deliveryDetails.date);
                const workingDayCount = WorkingDayBeforeCounter(
                    deliveryDate,
                    order.deliveryDetails.internalDeliveryDate,
                );
                setInternalDeliveryDaysAdvance(workingDayCount);
            } else if (order.deliveryDetails?.destination?._id) {
                getLabClientById({
                    variables: {
                        customerId: order.deliveryDetails.destination._id,
                    },
                });
            }
        }
    });

    useEffect(() => {
        if (order && !order.deliveryDetails?.internalDeliveryDate && childClientSettings?.internalDeliveryDaysAdvance) {
            const deliveryDate = new Date(order.deliveryDetails?.date);
            const advanceDate = getDeliveryAdvanceWorkingDate(
                deliveryDate,
                childClientSettings.internalDeliveryDaysAdvance as number,
            );
            if (advanceDate) {
                setInternalDeliveryDaysAdvance(childClientSettings.internalDeliveryDaysAdvance as number);
                setInternalDeliveryDate(advanceDate);
            }
        }
    }, [childClientSettings]);

    const [changeInternalDeliveryDate] = useMutation(updateInternalDeliveryDateTemplate, {
        onCompleted: ({ updateInternalDeliveryDate }) => {
            onClose();
            onUpdate?.(updateInternalDeliveryDate);
        },
        update(cache: ApolloCache<Order>, { data: { updateInternalDeliveryDate } }) {
            if (order && isUpdateCache) {
                const orderData = Object.assign({}, order);
                orderData.deliveryDetails = Object.assign({}, orderData.deliveryDetails, {
                    internalDeliveryDate: updateInternalDeliveryDate.deliveryDetails.internalDeliveryDate as Date,
                });
                updateCache(cache, orderData);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChangeInternalDeliveryDate = async () => {
        await changeInternalDeliveryDate({
            variables: {
                orderId: order?._id,
                internalDeliveryDate: internalDeliveryDate,
            },
        });
    };

    const handleInternalDeliveryDateChange = (date: Date | null) => {
        setInternalDeliveryDate(date);
    };

    if (labClientByIdLoading) {
        return <Loader />;
    }

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={onClose}
            title={translate('delivery-schedule:CHANGE_PRODUCTION_DEADLINE')}
            actions={
                <Button
                    disabled={!internalDeliveryDate}
                    variant="contained"
                    onClick={handleChangeInternalDeliveryDate}
                    data-test="change-internal-delivery-date-dialog.change-btn"
                >
                    {translate('delivery-schedule:CHANGE_PRODUCTION_DEADLINE')}
                </Button>
            }
        >
            <Box>
                <IntExtDeliveryDatePicker
                    label={translate('orders:INTERNAL_DELIVERY_DATE')}
                    selectedDate={internalDeliveryDate}
                    deliveryDaysAdvance={internalDeliveryDaysAdvance}
                    clinicDeliveryDate={order?.deliveryDetails?.date}
                    onChangeDate={handleInternalDeliveryDateChange}
                    dataTest="change-internal-delivery-date-dialog.internal-delivery-date-picker"
                />
            </Box>
        </DHDialog>
    );
};

export default ChangeInternalDeliveryDateDialog;
